/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useCustomerAuth, describeAxiosError, formatApiError } from "@/contexts/CustomerAuthContext";
import { Field } from "@/components/customer/AuthShell";
import SEO from "@/components/site/SEO";
import CustomerBottomNav from "@/components/customer/CustomerBottomNav";
import PhoneVerifyCard from "@/components/customer/PhoneVerifyCard";

export default function CustomerProfile() {
  const { user, logout, api } = useCustomerAuth();
  const nav = useNavigate();
  const [cur, setCur] = useState("");
  const [npw, setNpw] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (npw.length < 12 || !/[a-z]/.test(npw) || !/[A-Z]/.test(npw) || !/\d/.test(npw)) {
      setError("New password must be at least 12 chars and include uppercase, lowercase and a number.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/change-password", { current_password: cur, new_password: npw });
      toast.success("Password updated. Other devices have been signed out.");
      setCur(""); setNpw("");
    } catch (err) {
      setError(describeAxiosError(err, "Could not change password."));
    } finally { setLoading(false); }
  };

  const onLogout = async () => { await logout(); nav("/login", { replace: true }); };

  return (
    <div className="min-h-screen bg-brand-bgSoft text-brand-ink pb-24 md:pb-0" data-testid="customer-profile-page">
      <SEO title="My profile" path="/app/profile" />
      <header className="bg-white border-b border-brand-line">
        <div className="container-page flex items-center justify-between h-16">
          <Link to="/app" className="inline-flex items-center gap-2 text-sm font-semibold">
            <ArrowLeft className="h-4 w-4" /> Back to dashboard
          </Link>
          <button onClick={onLogout} className="text-xs font-semibold text-brand-muted hover:text-brand-ink">Sign out</button>
        </div>
      </header>

      <div className="container-page py-10 max-w-3xl space-y-8">
        <div>
          <p className="label-eyebrow">Profile</p>
          <h1 className="mt-3 font-display font-black text-3xl">{user?.name}</h1>
          <p className="mt-1 text-brand-muted">{user?.email}</p>
        </div>

        <section className="rounded-2xl border border-brand-line bg-white p-6 lg:p-8">
          <h2 className="font-display font-bold text-lg">Account</h2>          <dl className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <div><dt className="text-brand-muted text-xs uppercase tracking-[0.22em]">Country</dt><dd className="font-semibold">{user?.country || "—"}</dd></div>
            <div><dt className="text-brand-muted text-xs uppercase tracking-[0.22em]">Email verified</dt><dd className={`font-semibold ${user?.email_verified ? "text-brand-greenDark" : "text-red-600"}`}>{user?.email_verified ? "Yes" : "No"}</dd></div>
            <div><dt className="text-brand-muted text-xs uppercase tracking-[0.22em]">Marketing emails</dt><dd className="font-semibold">{user?.marketing_opt_in ? "Subscribed" : "Off"}</dd></div>
            <div><dt className="text-brand-muted text-xs uppercase tracking-[0.22em]">Account created</dt><dd className="font-semibold">{user?.created_at?.slice(0, 10)}</dd></div>
          </dl>
        </section>

        <PhoneVerifyCard />

        <section className="rounded-2xl border border-brand-line bg-white p-6 lg:p-8">
          <h2 className="font-display font-bold text-lg">Change password</h2>
          <p className="mt-1 text-sm text-brand-muted">All other devices will be signed out after a successful change.</p>
          <form onSubmit={onSubmit} className="mt-6 space-y-5" data-testid="change-password-form" noValidate>
            <Field label="Current password" type="password" testId="cp-current" value={cur} onChange={(e)=>setCur(e.target.value)}
                   required autoComplete="current-password" name="current" />
            <Field label="New password" type="password" testId="cp-new" value={npw} onChange={(e)=>setNpw(e.target.value)}
                   required autoComplete="new-password" name="new"
                   placeholder="Min 12 chars · upper, lower, number" />
            {error && <div data-testid="cp-error" className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}
            <button type="submit" disabled={loading} data-testid="cp-submit"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-green px-6 py-3 text-sm font-semibold text-brand-ink hover:bg-brand-greenHover disabled:opacity-70">
              {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Updating…</> : "Update password"}
            </button>
          </form>
        </section>
      </div>
      <CustomerBottomNav />
    </div>
  );
}
